<script setup>
import { ref, onMounted } from 'vue'

const CONTACT_EMAIL = 'info@krate.sh'
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.krate.sh'

// gate states: 'idle' | 'signed'
const gateState = ref('idle')
const confirmedBy = ref('')
const showModal = ref(false)

const name = ref('')
const email = ref('')
const company = ref('')
const submitting = ref(false)
const error = ref('')

onMounted(() => {
  const prior = sessionStorage.getItem('krate_nda')
  if (!prior) return
  const p = JSON.parse(prior)
  if (p.status === 'signed') {
    confirmedBy.value = `Agreed by ${p.name} (${p.email})${p.company ? ' · ' + p.company : ''}`
    gateState.value = 'signed'
  }
})

function openModal() {
  if (!name.value.trim() || !email.value.trim() || !company.value.trim()) {
    error.value = 'Please complete all fields before reviewing the terms.'
    return
  }
  error.value = ''
  showModal.value = true
}

async function accept() {
  submitting.value = true
  try {
    await fetch(`${API_BASE}/api/nda-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name.value.trim(),
        email: email.value.trim(),
        company: company.value.trim(),
      }),
    })
    const record = { name: name.value.trim(), email: email.value.trim(), company: company.value.trim(), status: 'signed' }
    sessionStorage.setItem('krate_nda', JSON.stringify(record))
    confirmedBy.value = `Agreed by ${record.name} (${record.email}) · ${record.company}`
    gateState.value = 'signed'
    showModal.value = false
  } catch {
    error.value = 'Something went wrong. Please try again.'
    showModal.value = false
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div style="background-color: var(--color-krate-dark-50); min-height: 100vh;">

    <!-- Terms modal -->
    <Teleport to="body">
      <div v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        style="background: rgba(0,0,0,0.55);"
        @click.self="showModal = false">
        <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col"
          style="max-height: 90vh;">
          <!-- Modal header -->
          <div class="px-8 pt-7 pb-4 border-b" style="border-color: var(--color-krate-dark-200);">
            <h2 class="text-lg font-bold" style="color: var(--color-krate-dark-900);">Confidentiality Agreement</h2>
            <p class="text-xs mt-1" style="color: var(--color-krate-dark-500);">Review and accept to access krate's security documents.</p>
          </div>

          <!-- Scrollable body -->
          <div class="overflow-y-auto px-8 py-5 text-sm leading-relaxed flex-1"
            style="color: var(--color-krate-dark-700);">
            <p class="font-semibold mb-4" style="color: var(--color-krate-dark-900);">CONFIDENTIALITY AGREEMENT (SECURITY DISCLOSURE)</p>

            <p class="mb-4">This Confidentiality Agreement ("Agreement") is entered into as of the date of electronic acceptance ("Effective Date") by and between <strong>krate, Inc.</strong>, a Delaware corporation ("Discloser"), and the individual or entity accessing the Trust Center ("Recipient").</p>

            <p class="font-semibold mb-1" style="color: var(--color-krate-dark-900);">1. Purpose</p>
            <p class="mb-4">Discloser maintains a Trust Center containing sensitive technical, security, infrastructure, and compliance information — including security policies, infrastructure architecture, and vulnerability management documentation — for prospective and current customers and business partners. Recipient requests access solely to evaluate Discloser's security posture for a potential or ongoing business relationship (the "Authorized Purpose").</p>

            <p class="font-semibold mb-1" style="color: var(--color-krate-dark-900);">2. Confidential Information</p>
            <p class="mb-4">"Confidential Information" means all technical and business information disclosed via the Trust Center, including security policies, data handling practices, infrastructure details, and compliance documentation, whether marked as confidential or not. Confidential Information does not include information that: (a) is or becomes publicly known through no fault of Recipient; (b) was already rightfully in Recipient's possession before access; or (c) is independently developed by Recipient without use of Discloser's information.</p>

            <p class="font-semibold mb-1" style="color: var(--color-krate-dark-900);">3. Recipient's Obligations</p>
            <p class="mb-4">Recipient agrees to: (a) hold Confidential Information in strict confidence; (b) restrict access to employees, contractors, or legal advisors who need it for the Authorized Purpose and are bound by equivalent confidentiality obligations; (c) use reasonable care to prevent unauthorized disclosure; and (d) not copy, distribute, or share the materials outside Recipient's organization without Discloser's prior written consent.</p>

            <p class="font-semibold mb-1" style="color: var(--color-krate-dark-900);">4. Term</p>
            <p class="mb-4">Recipient's right to access the Trust Center may be revoked by Discloser at any time. Recipient's obligation to protect Confidential Information survives for three (3) years from the date of acceptance.</p>

            <p class="font-semibold mb-1" style="color: var(--color-krate-dark-900);">5. Governing Law</p>
            <p class="mb-4">This Agreement is governed by the laws of the State of Delaware, without regard to conflict of law principles.</p>

            <p class="font-semibold mb-1" style="color: var(--color-krate-dark-900);">6. Remedies</p>
            <p class="mb-4">Recipient acknowledges that unauthorized disclosure may cause irreparable harm to Discloser. Discloser is entitled to seek injunctive relief to prevent breaches, in addition to any other available remedies.</p>

            <p class="text-xs pt-2 border-t" style="border-color: var(--color-krate-dark-200); color: var(--color-krate-dark-500);">By clicking "I Accept," Recipient agrees to be bound by this Agreement.</p>
          </div>

          <!-- Modal footer -->
          <div class="px-8 py-5 border-t flex items-center justify-end gap-3" style="border-color: var(--color-krate-dark-200);">
            <button @click="showModal = false"
              class="px-5 py-2.5 rounded-lg text-sm font-semibold transition"
              style="color: var(--color-krate-dark-600); background: var(--color-krate-dark-100);">
              Cancel
            </button>
            <button @click="accept" :disabled="submitting"
              class="px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition disabled:opacity-50"
              style="background-color: var(--color-krate-blue-600);">
              {{ submitting ? 'Recording…' : 'I Accept — Get Access' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Hero -->
    <section class="pt-24 pb-14 border-b bg-white" style="border-color: var(--color-krate-dark-200);">
      <div class="max-w-4xl mx-auto px-6">
        <div class="flex items-center gap-3 mb-4">
          <span class="inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-3 py-1.5 border"
            style="background-color: #f0fdf4; color: #15803d; border-color: #bbf7d0;">
            <span class="h-1.5 w-1.5 rounded-full" style="background-color: #22c55e;"></span>
            Security posture: current
          </span>
        </div>
        <h1 class="text-4xl sm:text-5xl font-extrabold tracking-tight" style="color: var(--color-krate-dark-900);">
          krate Trust Center
        </h1>
        <p class="mt-4 text-lg max-w-2xl leading-relaxed" style="color: var(--color-krate-dark-600);">
          Security policies, data handling practices, and compliance documentation for krate customers and prospective partners.
          Documents are shared under a confidentiality agreement.
        </p>
        <p class="mt-3 text-sm" style="color: var(--color-krate-dark-500);">
          Questions? <a :href="`mailto:${CONTACT_EMAIL}`" class="hover:underline" style="color: var(--color-krate-blue-600);">{{ CONTACT_EMAIL }}</a>
        </p>
      </div>
    </section>

    <!-- Security overview cards -->
    <section class="py-14">
      <div class="max-w-4xl mx-auto px-6">
        <h2 class="text-xl font-bold mb-8" style="color: var(--color-krate-dark-900);">Security overview</h2>
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="item in [
            { icon: 'shield', label: 'Data handling', status: 'Documented', green: true },
            { icon: 'lock',   label: 'Access control', status: 'Implemented', green: true },
            { icon: 'cloud',  label: 'Infrastructure', status: 'Vultr / self-hosted', green: true },
            { icon: 'alert',  label: 'Incident response', status: 'Policy in place', green: true },
            { icon: 'check',  label: 'Vulnerability mgmt', status: 'Continuous', green: true },
            { icon: 'box',    label: 'Container security', status: 'Hardened & scanned', green: true },
          ]" :key="item.label"
            class="bg-white rounded-xl border p-5 flex items-start gap-4"
            style="border-color: var(--color-krate-dark-200);">
            <div class="flex-shrink-0 h-9 w-9 rounded-lg flex items-center justify-center"
              style="background-color: #f0fdf4; color: #16a34a;">
              <svg v-if="item.icon === 'shield'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
              <svg v-if="item.icon === 'lock'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
              <svg v-if="item.icon === 'cloud'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/></svg>
              <svg v-if="item.icon === 'alert'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
              <svg v-if="item.icon === 'check'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              <svg v-if="item.icon === 'box'" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
            </div>
            <div>
              <div class="text-sm font-semibold" style="color: var(--color-krate-dark-900);">{{ item.label }}</div>
              <div class="text-xs mt-0.5 font-medium" style="color: #16a34a;">{{ item.status }}</div>
            </div>
          </div>
        </div>

        <!-- Infrastructure note -->
        <div class="mt-6 rounded-xl border bg-white p-6" style="border-color: var(--color-krate-dark-200);">
          <p class="text-xs font-semibold uppercase tracking-wide mb-3" style="color: var(--color-krate-dark-500);">Infrastructure</p>
          <p class="text-sm leading-relaxed" style="color: var(--color-krate-dark-600);">
            krate's server infrastructure runs on <strong style="color: var(--color-krate-dark-800);">Vultr</strong> (US region).
            The Solo tier is fully local — workloads run on your Mac and no data is sent to krate servers.
            Team and Enterprise tiers use your hosted or self-hosted krate server. See the
            <router-link to="/privacy" class="hover:underline" style="color: var(--color-krate-blue-600);">Privacy Policy</router-link>
            for full data residency details.
          </p>
        </div>
      </div>
    </section>

    <!-- NDA Gate -->
    <section id="documents" class="pb-20">
      <div class="max-w-4xl mx-auto px-6">

        <!-- State: idle — show the form -->
        <div v-if="gateState === 'idle'" class="rounded-2xl border-2 border-dashed bg-white p-10 text-center"
          style="border-color: var(--color-krate-dark-300);">
          <div class="inline-flex items-center justify-center h-14 w-14 rounded-full mb-5 mx-auto"
            style="background-color: var(--color-krate-dark-900); color: white;">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <h2 class="text-2xl font-bold" style="color: var(--color-krate-dark-900);">Policy documents require a confidentiality agreement</h2>
          <p class="mt-3 text-sm leading-relaxed max-w-lg mx-auto" style="color: var(--color-krate-dark-600);">
            Enter your details, review our brief confidentiality terms, and get instant access to krate's security policy documents.
          </p>

          <form @submit.prevent="openModal" class="mt-8 max-w-sm mx-auto space-y-4 text-left">
            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide" style="color: var(--color-krate-dark-600);">Your name</span>
              <input v-model="name" type="text" required placeholder="Jane Smith"
                class="mt-1 w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition"
                style="border-color: var(--color-krate-dark-300); color: var(--color-krate-dark-900);"
                @focus="$event.target.style.borderColor = 'var(--color-krate-blue-500)'"
                @blur="$event.target.style.borderColor = 'var(--color-krate-dark-300)'" />
            </label>
            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide" style="color: var(--color-krate-dark-600);">Company email</span>
              <input v-model="email" type="email" required placeholder="jane@yourcompany.com"
                class="mt-1 w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition"
                style="border-color: var(--color-krate-dark-300); color: var(--color-krate-dark-900);"
                @focus="$event.target.style.borderColor = 'var(--color-krate-blue-500)'"
                @blur="$event.target.style.borderColor = 'var(--color-krate-dark-300)'" />
            </label>
            <label class="block">
              <span class="text-xs font-semibold uppercase tracking-wide" style="color: var(--color-krate-dark-600);">Company</span>
              <input v-model="company" type="text" required placeholder="Acme Corp"
                class="mt-1 w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition"
                style="border-color: var(--color-krate-dark-300); color: var(--color-krate-dark-900);"
                @focus="$event.target.style.borderColor = 'var(--color-krate-blue-500)'"
                @blur="$event.target.style.borderColor = 'var(--color-krate-dark-300)'" />
            </label>

            <p v-if="error" class="text-xs font-medium text-center" style="color: #dc2626;">{{ error }}</p>

            <button type="submit"
              class="w-full rounded-lg font-semibold py-3 text-sm text-white transition"
              style="background-color: var(--color-krate-blue-600);">
              View Terms &amp; Get Access
            </button>
          </form>
        </div>

        <!-- State: signed — show vault -->
        <div v-else-if="gateState === 'signed'">
          <div class="flex items-center gap-3 mb-6">
            <div class="h-8 w-8 rounded-full flex items-center justify-center" style="background-color: #dcfce7;">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: #16a34a;">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <div>
              <p class="text-sm font-semibold" style="color: var(--color-krate-dark-900);">Access granted</p>
              <p class="text-xs" style="color: var(--color-krate-dark-500);">{{ confirmedBy }}</p>
            </div>
          </div>

          <div class="flex items-center gap-5 bg-white rounded-xl border p-6"
            style="border-color: var(--color-krate-dark-200);">
            <div class="flex-shrink-0 h-14 w-14 rounded-xl flex items-center justify-center"
              style="background-color: #eff6ff; color: var(--color-krate-blue-600);">
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <a href="/trust/krate-security-policies.html" target="_blank" rel="noopener"
                class="font-bold hover:underline" style="color: var(--color-krate-blue-600);">
                krate.sh/trust/krate-security-policies.html
              </a>
              <div class="text-sm mt-1" style="color: var(--color-krate-dark-500);">Security policies & compliance documentation — cover page, table of contents, and all 6 policies.</div>
              <div class="flex flex-wrap gap-2 mt-3">
                <span v-for="tag in ['Information Security', 'Data Handling', 'Incident Response', 'Vulnerability Mgmt', 'BCDR', 'Subprocessors']"
                  :key="tag" class="text-xs rounded-full px-2.5 py-0.5"
                  style="background-color: var(--color-krate-dark-100); color: var(--color-krate-dark-600);">
                  {{ tag }}
                </span>
              </div>
            </div>
            <div class="flex-shrink-0 text-right">
              <div class="text-xs mb-2" style="color: var(--color-krate-dark-400);">Updated June 2026</div>
              <a href="/trust/krate-security-policies.html?print=1" target="_blank" rel="noopener"
                class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition"
                style="background-color: var(--color-krate-blue-600); color: white;"
                @click.stop>
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                </svg>
                Download PDF
              </a>
            </div>
          </div>

          <div class="mt-8 rounded-xl border bg-white p-5 text-sm" style="border-color: var(--color-krate-dark-200); color: var(--color-krate-dark-600);">
            <span class="font-semibold" style="color: var(--color-krate-dark-900);">Need something specific?</span>
            If you need a completed security questionnaire (CAIQ, SIG, custom), a Trivy CVE scan report, or a CycloneDX SBOM,
            email <a :href="`mailto:${CONTACT_EMAIL}`" class="hover:underline" style="color: var(--color-krate-blue-600);">{{ CONTACT_EMAIL }}</a>.
          </div>
        </div>

      </div>
    </section>

  </div>
</template>
